import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "./utils/cn";
import Label from "./Label";
import FloatingLabel from "./FloatingLabel";
import FieldError from "./FieldError";

type NativeProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "size" | "type"
>;

export interface TextFieldProps extends NativeProps {
  value?: string | number;
  onChange: (value: string) => void;
  label?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  type?: "text" | "email" | "tel" | "password" | "search" | "url" | "date";
  /** Prefix adornment shown at the start of the field (e.g. search icon). */
  prefix?: ReactNode;
  /** Suffix unit / adornment shown at the end of the field. */
  sign?: ReactNode;
  /** Alignment of the typed value. Defaults to `start` (natural reading). */
  align?: "start" | "end";
  /**
   * `default` — label sits above the field.
   * `compact` — label sits ON the top border (notched-outline look), saving a
   *   row of vertical space. Mirrors the Historic Worth dropdowns.
   */
  variant?: "default" | "compact";
  containerClassName?: string;
}

/**
 * Canonical single-line text field. Composes a label + input + FieldError and
 * standardizes the 15+ ad-hoc input styles onto the design tokens.
 */
const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      value,
      onChange,
      label,
      required,
      disabled,
      error,
      placeholder,
      type = "text",
      prefix,
      sign,
      align = "start",
      variant = "default",
      id,
      className,
      containerClassName,
      ...rest
    },
    ref
  ) => {
    const reactId = useId();
    const inputId = id ?? reactId;
    const errorId = error ? `${inputId}-error` : undefined;
    const compact = variant === "compact";

    const input = (
      <input
        ref={ref}
        id={inputId}
        type={type}
        value={value ?? ""}
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
        aria-label={compact && typeof label === "string" ? label : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          "w-full bg-transparent text-menu placeholder:text-menu/35 focus:outline-none disabled:cursor-not-allowed",
          type === "search" &&
            "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none",
          align === "end" ? "text-end" : "text-start",
          className
        )}
        {...rest}
      />
    );

    const adornments = (
      <>
        {prefix && (
          <span className="shrink-0 text-menu/50" aria-hidden>
            {prefix}
          </span>
        )}
        {input}
        {sign && <span className="shrink-0 text-menu/70">{sign}</span>}
      </>
    );

    if (compact) {
      return (
        <div>
          <div
            className={cn(
              "transition-colors group relative flex h-control items-center gap-2 rounded-control border bg-surface px-3",
              error
                ? "border-danger"
                : "border-border-subtle focus-within:border-secondary",
              disabled && "opacity-60",
              containerClassName
            )}
          >
            {label && (
              <FloatingLabel
                htmlFor={inputId}
                required={required}
                error={Boolean(error)}
              >
                {label}
              </FloatingLabel>
            )}
            {adornments}
          </div>
          <FieldError id={errorId}>{error}</FieldError>
        </div>
      );
    }

    return (
      <div>
        {label && (
          <Label htmlFor={inputId} required={required} readOnly={disabled}>
            {label}
          </Label>
        )}
        <div
          className={cn(
            "transition flex h-control items-center gap-2 rounded-control border bg-surface px-3",
            error
              ? "border-danger focus-within:border-danger"
              : "border-border-subtle focus-within:border-secondary",
            "focus-within:ring-2 focus-within:ring-secondary/15 focus-within:ring-offset-0",
            disabled && "opacity-60",
            containerClassName
          )}
        >
          {adornments}
        </div>
        <FieldError id={errorId}>{error}</FieldError>
      </div>
    );
  }
);

TextField.displayName = "TextField";

export default TextField;
